import React, { useState, useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { toast } from "@/hooks/use-toast";
import { api } from "@/api";
import { formatDateEU, formatError } from "@/components/shared/helpers";

const MONTH_LABELS = ["Janvier", "Février", "Mars", "Avril", "Mai", "Juin", "Juillet", "Août", "Septembre", "Octobre", "Novembre", "Décembre"];

export default function AdminTasks({ properties }) {
  const [propertyId, setPropertyId] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [summary, setSummary] = useState(null);

  // ─── Génération facture ─────────────────────────────────────────────────
  const [genOpen, setGenOpen] = useState(false);
  const [genEntity, setGenEntity] = useState("BDM"); // "BDM" | "BDM_PARTICULIER"
  const [genInvoiceNumber, setGenInvoiceNumber] = useState("");
  const [genIssueDate, setGenIssueDate] = useState(new Date().toISOString().slice(0, 10));
  const [genPeriodLabel, setGenPeriodLabel] = useState("");
  const [genNotes, setGenNotes] = useState("");
  const [genSubmitting, setGenSubmitting] = useState(false);
  // ─── Flux 2 étapes : génération puis envoi ─────────────────────────────
  const [generatedInvoice, setGeneratedInvoice] = useState(null); // { id, amount_ttc, pdf_url }
  const [confirmCancelOpen, setConfirmCancelOpen] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [sendingEmail, setSendingEmail] = useState(false);
  // Lignes manuelles ajoutées par l'admin via "+ Ajouter une ligne"
  const [manualLines, setManualLines] = useState([]); // [{description, date, qty, unit_price_ttc}]
  // Édition d'une ligne manuelle persistée (depuis l'extraction)
  const [editMlOpen, setEditMlOpen] = useState(false);
  const [editMl, setEditMl] = useState(null); // {id, description, qty, unit_price_ttc, comment}

  // Lignes auto-générées depuis summary selon l'entité sélectionnée
  const summaryLines = useMemo(() => {
    if (!summary) return [];
    const source = genEntity === "BDM_PARTICULIER"
      ? (summary.lines_particulier || [])
      : (summary.lines || []);
    return source.map((t) => ({
      id: t.id,
      description: t.task_type,
      comment: t.comment || "",
      date: t.date || null,
      qty: 1,
      unit_price_ttc: Number(t.price_ttc) || 0,
      is_free: !Number(t.price_ttc),
    }));
  }, [summary, genEntity]);

  // Lignes totales (auto + manuelles)
  const genLines = useMemo(() => {
    const manual = manualLines.map((m) => ({
      id: m._tmp_id,
      description: m.description || "Prestation",
      comment: m.comment || (m.date ? `Le ${formatDateEU(m.date)}` : ""),
      date: m.date || null,
      qty: Number(m.qty) || 0,
      unit_price_ttc: Number(m.unit_price_ttc) || 0,
      is_free: !Number(m.unit_price_ttc),
      manual: true,
    }));
    return [...summaryLines, ...manual];
  }, [summaryLines, manualLines]);

  const VAT_RATE_BY_ENTITY = { "BDM": 0.20, "BDM_PARTICULIER": 0.10 };
  const vatRate = VAT_RATE_BY_ENTITY[genEntity] || 0.20;
  const lineHt = (l) => l.is_free ? 0 : (l.qty * l.unit_price_ttc) / (1 + vatRate);
  const lineTtc = (l) => l.is_free ? 0 : l.qty * l.unit_price_ttc;
  const totalsHT = genLines.reduce((acc, l) => acc + lineHt(l), 0);
  const totalsTTC = genLines.reduce((acc, l) => acc + lineTtc(l), 0);
  const totalsTVA = totalsTTC - totalsHT;
  const vatPct = Math.round(vatRate * 100);

  const openGenerateDialog = (entity = "BDM") => {
    if (!summary) return;
    setGenEntity(entity);
    let label = "";
    if (summary.from_date) {
      const m = parseInt(summary.from_date.slice(5, 7), 10) - 1;
      const y = summary.from_date.slice(0, 4);
      if (m >= 0 && m < 12) label = `${MONTH_LABELS[m]} ${y}`;
    }
    setGenPeriodLabel(label);
    setGenInvoiceNumber("");
    setGenIssueDate(new Date().toISOString().slice(0, 10));
    setGenNotes("");
    setManualLines([]);
    setGenOpen(true);
  };

  const addManualLine = () => {
    const tmpId = `tmp-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    setManualLines((prev) => [...prev, { _tmp_id: tmpId, description: "", date: new Date().toISOString().slice(0, 10), qty: 1, unit_price_ttc: 0, comment: "" }]);
  };
  const updateManualLine = (idx, patch) => {
    setManualLines((prev) => prev.map((l, i) => (i === idx ? { ...l, ...patch } : l)));
  };
  const removeManualLine = (idx) => {
    setManualLines((prev) => prev.filter((_, i) => i !== idx));
  };

  const submitGenerate = async () => {
    if (!genInvoiceNumber.trim()) {
      toast({ title: "N° de facture obligatoire", variant: "destructive" });
      return;
    }
    if (!genLines.filter((l) => !l.is_free).length) {
      toast({ title: "Aucune ligne facturable", description: "Toutes les lignes sont à 0 €.", variant: "destructive" });
      return;
    }
    setGenSubmitting(true);
    try {
      const payload = {
        property_id: propertyId,
        invoice_number: genInvoiceNumber.trim(),
        issue_date: genIssueDate || null,
        period_label: genPeriodLabel || "",
        from_date: summary.from_date || null,
        to_date: summary.to_date || null,
        lines: genLines,
        notes: genNotes || "",
        entity: genEntity,
      };
      // Étape 1 : génération du PDF SANS envoi email (send_email=false)
      const res = await api.post("/admin/invoices/generate?send_email=false", payload);
      const invId = res.data.invoice_id;
      // Récupère le PDF en blob pour aperçu inline
      const pdfRes = await api.get(`/admin/invoices/${invId}/download`, { responseType: "blob" });
      const blob = new Blob([pdfRes.data], { type: "application/pdf" });
      const pdfUrl = URL.createObjectURL(blob);
      setGeneratedInvoice({ id: invId, amount_ttc: res.data.amount_ttc || 0, pdf_url: pdfUrl, pdf_blob: blob });
      toast({ title: "✅ Facture générée", description: `${(res.data.amount_ttc || 0).toFixed(2)} € TTC · Vérifiez l'aperçu avant envoi` });
    } catch (e) {
      toast({ title: "Erreur génération", description: formatError(e), variant: "destructive" });
    } finally {
      setGenSubmitting(false);
    }
  };

  const sendInvoiceEmail = async () => {
    if (!generatedInvoice?.id) return;
    setSendingEmail(true);
    try {
      await api.post(`/admin/invoices/${generatedInvoice.id}/send-notification`);
      toast({ title: "📧 Email envoyé", description: "La facture a été envoyée au propriétaire." });
      // Marque la facture comme envoyée pour adapter le warning de l'annulation
      setGeneratedInvoice((prev) => prev ? { ...prev, email_sent: true } : prev);
      // Refresh + ferme la modale
      try {
        const sres = await api.get("/admin/reports/tasks-summary", { params: { property_id: propertyId, from_date: fromDate, to_date: toDate } });
        setSummary(sres.data);
      } catch (err) {
        console.warn("Failed to refresh tasks summary after invoice send", err);
      }
      closeGenerateDialog();
    } catch (e) {
      toast({ title: "Envoi impossible", description: formatError(e), variant: "destructive" });
    } finally {
      setSendingEmail(false);
    }
  };

  const downloadGeneratedInvoice = () => {
    if (!generatedInvoice?.pdf_url) return;
    const a = document.createElement("a");
    a.href = generatedInvoice.pdf_url;
    a.download = `Facture_${genInvoiceNumber || generatedInvoice.id}.pdf`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const cancelGeneratedInvoice = () => {
    // Ouvre l'AlertDialog Radix (plus fiable que window.confirm sur mobile/PWA/prod)
    if (!generatedInvoice?.id) return;
    setConfirmCancelOpen(true);
  };

  const performCancelInvoice = async () => {
    if (!generatedInvoice?.id) return;
    setCancelling(true);
    try {
      await api.delete(`/admin/invoices/${generatedInvoice.id}`);
      toast({ title: "Facture annulée", description: "La facture a été supprimée et l'action est tracée dans l'audit log." });
      setConfirmCancelOpen(false);
      closeGenerateDialog();
      try {
        const sres = await api.get("/admin/reports/tasks-summary", { params: { property_id: propertyId, from_date: fromDate, to_date: toDate } });
        setSummary(sres.data);
      } catch (err) {
        console.warn("Failed to refresh tasks summary after invoice cancellation", err);
      }
    } catch (e) {
      console.error("cancelGeneratedInvoice failed:", e);
      toast({ title: "Suppression impossible", description: formatError(e), variant: "destructive" });
    } finally {
      setCancelling(false);
    }
  };

  const closeGenerateDialog = () => {
    if (generatedInvoice?.pdf_url) {
      URL.revokeObjectURL(generatedInvoice.pdf_url);
    }
    setGeneratedInvoice(null);
    setGenOpen(false);
  };

  const handlePropertyChange = (newPropertyId) => {
    setPropertyId(newPropertyId);
    setSummary(null);
  };

  const load = async () => {
    if (!propertyId || !fromDate || !toDate) {
      toast({ title: "Champs manquants", description: "Choisissez logement + dates.", variant: "destructive" });
      return;
    }
    try {
      const res = await api.get("/admin/reports/tasks-summary", { params: { property_id: propertyId, from_date: fromDate, to_date: toDate } });
      setSummary(res.data);
    } catch (e) {
      toast({ title: "Erreur", description: formatError(e), variant: "destructive" });
    }
  };

  const exportCSV = () => {
    if (!summary) return;
    let csv = "AIDE À LA FACTURATION\n";
    csv += `Logement;${summary.property_name}\n`;
    csv += `Période;${formatDateEU(summary.from_date)} au ${formatDateEU(summary.to_date)}\n`;
    csv += `Total TTC;${summary.total} €\n\n`;
    csv += "Date;Type;Prix TTC;Commentaire\n";
    (summary.lines || []).forEach(line => {
      csv += `${formatDateEU(line.date)};${line.task_type};${line.price_ttc} €;${(line.comment || "").replace(/;/g, ",")}\n`;
    });
    const blob = new Blob(["\ufeff" + csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `facturation_${summary.property_name.replace(/[^a-zA-Z0-9]/g, "_")}_${formatDateEU(fromDate).replace(/\//g, '-')}_${formatDateEU(toDate).replace(/\//g, '-')}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast({ title: "Export terminé", description: "Le fichier CSV a été téléchargé." });
  };

  const openEditManualLine = (line) => {
    setEditMl({
      id: line.manual_line_id,
      description: line.task_type || "",
      qty: line.qty || 1,
      unit_price_ttc: line.unit_price_ttc || 0,
      comment: line.comment || "",
    });
    setEditMlOpen(true);
  };

  const saveEditedManualLine = async () => {
    if (!editMl?.id) return;
    try {
      await api.put(`/admin/manual-lines/${editMl.id}`, {
        description: editMl.description,
        qty: Number(editMl.qty) || 0,
        unit_price_ttc: Number(editMl.unit_price_ttc) || 0,
        comment: editMl.comment || "",
      });
      toast({ title: "Ligne mise à jour" });
      setEditMlOpen(false);
      setEditMl(null);
      const sres = await api.get("/admin/reports/tasks-summary", { params: { property_id: propertyId, from_date: fromDate, to_date: toDate } });
      setSummary(sres.data);
    } catch (e) {
      toast({ title: "Mise à jour impossible", description: formatError(e), variant: "destructive" });
    }
  };

  const deletePersistedManualLine = async (line) => {
    if (!line.manual_line_id) return;
    if (!window.confirm(`Supprimer la ligne manuelle "${line.task_type}" (${line.price_ttc} €) ?`)) return;
    try {
      await api.delete(`/admin/manual-lines/${line.manual_line_id}`);
      toast({ title: "Ligne supprimée" });
      const sres = await api.get("/admin/reports/tasks-summary", { params: { property_id: propertyId, from_date: fromDate, to_date: toDate } });
      setSummary(sres.data);
    } catch (e) {
      toast({ title: "Suppression impossible", description: formatError(e), variant: "destructive" });
    }
  };


  return (
    <div data-testid="admin-tasks" className="space-y-4">
      <Card data-testid="admin-tasks-card" className="border-white/10 bg-white/[0.08]">
        <CardHeader>
          <CardTitle data-testid="admin-tasks-title" className="text-slate-50">Extraction prestations réalisées</CardTitle>
          <div data-testid="admin-tasks-subtitle" className="text-sm text-slate-200">
            Base = tâches réalisées (vous avez la main sur le réel). Total = somme des prix TTC saisis.
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="grid grid-cols-1 gap-2 md:grid-cols-4">
            <div className="md:col-span-2 space-y-1">
              <Label data-testid="admin-tasks-property-label">Logement</Label>
              <select data-testid="admin-tasks-property-select" className="flex h-9 w-full rounded-md border border-white/10 bg-white/[0.08] px-3 py-2 text-sm text-slate-50" value={propertyId} onChange={(e) => handlePropertyChange(e.target.value)}>
                <option value="" data-testid="admin-tasks-property-option-empty">Choisir…</option>
                {properties.filter((p) => !p.archived).map((p) => (<option key={p.id} value={p.id} data-testid={`admin-tasks-property-option-${p.id}`}>{p.short_name}</option>))}
              </select>
            </div>
            <div className="space-y-1">
              <Label data-testid="admin-tasks-from-label">Du</Label>
              <Input data-testid="admin-tasks-from-input" type="date" className="border-white/10 bg-white/[0.08]" value={fromDate} onChange={(e) => setFromDate(e.target.value)} />
            </div>
            <div className="space-y-1">
              <Label data-testid="admin-tasks-to-label">Au</Label>
              <Input data-testid="admin-tasks-to-input" type="date" className="border-white/10 bg-white/[0.08]" value={toDate} onChange={(e) => setToDate(e.target.value)} />
            </div>
          </div>

          <div className="flex gap-2 flex-wrap">
            <Button data-testid="admin-tasks-load-button" className="rounded-full bg-emerald-500 text-white hover:bg-emerald-400" onClick={load}>Générer</Button>
            {summary && (
              <Button data-testid="admin-tasks-export-button" variant="outline" className="rounded-full border-white/10 bg-white/[0.08] text-slate-50 hover:bg-white/10" onClick={exportCSV}>Exporter CSV</Button>
            )}
            {summary && (summary.lines || []).length > 0 && (
              <Button data-testid="admin-tasks-generate-invoice-button" className="rounded-full bg-violet-500 text-white hover:bg-violet-400" onClick={() => openGenerateDialog("BDM")}>📄 Générer la facture BDM</Button>
            )}
            {summary && summary.owned_by_individual && (summary.lines_particulier || []).length > 0 && (
              <Button data-testid="admin-tasks-generate-invoice-particulier-button" className="rounded-full bg-fuchsia-500 text-white hover:bg-fuchsia-400" onClick={() => openGenerateDialog("BDM_PARTICULIER")}>📄 Générer la facture BDM Particulier</Button>
            )}
          </div>

          {summary && (
            <div data-testid="admin-tasks-summary" className="space-y-3">
              <div className="rounded-xl border border-white/10 bg-white/[0.08] p-3">
                <div data-testid="admin-tasks-summary-title" className="text-sm text-slate-50">{summary.property_name}</div>
                <div data-testid="admin-tasks-summary-total" className="mt-1 text-2xl font-semibold">Total global: {summary.total} €</div>
                {summary.owned_by_individual && (
                  <div className="mt-1 flex gap-4 text-sm">
                    <span className="text-slate-300">Facturation BDM : <b className="text-white">{summary.total_standard} €</b></span>
                    <span className="text-violet-300">Facturation BDM Particulier : <b className="text-violet-200">{summary.total_particulier} €</b></span>
                  </div>
                )}
                <div data-testid="admin-tasks-summary-range" className="text-xs text-slate-200">Période: {formatDateEU(summary.from_date)} → {formatDateEU(summary.to_date)}</div>
              </div>

              {/* Bloc standard */}
              <div>
                <h3 className="text-sm font-medium text-slate-200 mb-2">
                  {summary.owned_by_individual ? "Facturation BDM" : "Prestations"}
                  {summary.owned_by_individual && <span className="ml-2 text-xs text-slate-400">Sous-total: {summary.total_standard} €</span>}
                </h3>
                <div className="rounded-xl border border-white/10 bg-white/[0.08]">
                  <Table data-testid="admin-tasks-lines-table">
                    <TableHeader>
                      <TableRow>
                        <TableHead data-testid="admin-tasks-lines-col-date">Date</TableHead>
                        <TableHead data-testid="admin-tasks-lines-col-type">Type</TableHead>
                        <TableHead data-testid="admin-tasks-lines-col-price">Prix TTC</TableHead>
                        <TableHead data-testid="admin-tasks-lines-col-comment">Commentaire</TableHead>
                        <TableHead data-testid="admin-tasks-lines-col-actions" className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {(summary.lines || []).map((t) => (
                        <TableRow key={t.id} data-testid={`admin-task-line-${t.id}`} className={t.manual ? "bg-violet-500/5" : ""}>
                          <TableCell data-testid={`admin-task-line-date-${t.id}`}>{formatDateEU(t.date)}</TableCell>
                          <TableCell data-testid={`admin-task-line-type-${t.id}`}>
                            {t.task_type}
                            {t.manual && <span data-testid={`admin-task-line-manual-badge-${t.id}`} className="ml-2 inline-block rounded-full bg-violet-500/20 px-2 py-0.5 text-[10px] font-medium text-violet-300 border border-violet-400/40">Manuel</span>}
                          </TableCell>
                          <TableCell data-testid={`admin-task-line-price-${t.id}`}>{t.price_ttc} €</TableCell>
                          <TableCell data-testid={`admin-task-line-comment-${t.id}`}>{t.comment || ""}</TableCell>
                          <TableCell className="text-right">
                            {t.manual && t.manual_line_id && (
                              <div className="flex justify-end gap-1">
                                <Button
                                  data-testid={`admin-task-line-edit-${t.id}`}
                                  size="sm"
                                  variant="outline"
                                  title="Modifier cette ligne manuelle"
                                  className="h-7 px-2 text-xs border-violet-500/40 bg-violet-500/10 text-violet-200 hover:bg-violet-500/20"
                                  onClick={() => openEditManualLine(t)}
                                >
                                  ✏️
                                </Button>
                                <Button
                                  data-testid={`admin-task-line-delete-${t.id}`}
                                  size="sm"
                                  variant="outline"
                                  title="Supprimer cette ligne manuelle"
                                  className="h-7 px-2 text-xs border-rose-500/40 bg-rose-500/10 text-rose-200 hover:bg-rose-500/20"
                                  onClick={() => deletePersistedManualLine(t)}
                                >
                                  🗑️
                                </Button>
                              </div>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                      {(!summary.lines || summary.lines.length === 0) && (
                        <TableRow><TableCell data-testid="admin-tasks-lines-empty" colSpan={5} className="py-8 text-center text-slate-200">Aucune tâche sur la période.</TableCell></TableRow>
                      )}
                    </TableBody>
                  </Table>
                </div>
              </div>

              {/* Bloc BDM Particulier - uniquement si logement en nom propre et lignes existantes */}
              {summary.owned_by_individual && summary.lines_particulier && summary.lines_particulier.length > 0 && (
                <div>
                  <h3 className="text-sm font-medium text-violet-300 mb-2">
                    Facturation BDM Particulier (crédit d&apos;impôt 50%)
                    <span className="ml-2 text-xs text-violet-400">Sous-total: {summary.total_particulier} €</span>
                  </h3>
                  <div className="rounded-xl border border-violet-500/20 bg-violet-500/5">
                    <Table data-testid="admin-tasks-particulier-table">
                      <TableHeader>
                        <TableRow>
                          <TableHead>Date</TableHead>
                          <TableHead>Type</TableHead>
                          <TableHead>Prix TTC</TableHead>
                          <TableHead>Commentaire</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {summary.lines_particulier.map((t) => (
                          <TableRow key={t.id} data-testid={`admin-task-particulier-${t.id}`} className={t.manual ? "bg-violet-500/10" : ""}>
                            <TableCell>{formatDateEU(t.date)}</TableCell>
                            <TableCell>
                              {t.task_type}
                              {t.manual && <span className="ml-2 inline-block rounded-full bg-violet-500/20 px-2 py-0.5 text-[10px] font-medium text-violet-300 border border-violet-400/40">Manuel</span>}
                            </TableCell>
                            <TableCell className="text-violet-300">{t.price_ttc} €</TableCell>
                            <TableCell>{t.comment || ""}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Dialog : Génération facture */}
      <Dialog open={genOpen} onOpenChange={(open) => { if (!open) { closeGenerateDialog(); } else { setGenOpen(true); } }}>
        <DialogContent data-testid="admin-tasks-generate-dialog" className="border-white/10 bg-[#0f1320] text-slate-100 w-[calc(100vw-2rem)] max-w-4xl mx-auto max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-slate-50">
              Générer une facture · <span className={genEntity === "BDM_PARTICULIER" ? "text-fuchsia-300" : "text-violet-300"}>{genEntity === "BDM_PARTICULIER" ? "BDM Particulier" : "BDM"}</span>
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
              <div className="space-y-1">
                <Label>N° facture *</Label>
                <Input data-testid="admin-tasks-gen-number" className="border-white/10 bg-white/[0.08]" placeholder="Ex: 2026-05-001" value={genInvoiceNumber} onChange={(e) => setGenInvoiceNumber(e.target.value)} />
              </div>
              <div className="space-y-1">
                <Label>Date d&apos;émission</Label>
                <Input data-testid="admin-tasks-gen-issue-date" type="date" className="border-white/10 bg-white/[0.08]" value={genIssueDate} onChange={(e) => setGenIssueDate(e.target.value)} />
              </div>
              <div className="space-y-1">
                <Label>Libellé période</Label>
                <Input data-testid="admin-tasks-gen-period-label" className="border-white/10 bg-white/[0.08]" placeholder="Ex: Mai 2026" value={genPeriodLabel} onChange={(e) => setGenPeriodLabel(e.target.value)} />
              </div>
            </div>

            <div className="space-y-1">
              <Label>Informations complémentaires (optionnel)</Label>
              <textarea data-testid="admin-tasks-gen-notes" className="flex w-full min-h-[70px] rounded-md border border-white/10 bg-white/[0.08] px-3 py-2 text-sm text-slate-50" value={genNotes} onChange={(e) => setGenNotes(e.target.value)} />
            </div>

            {/* Section : lignes manuelles supplémentaires */}
            <div className="space-y-2 rounded-xl border border-violet-500/20 bg-violet-500/5 p-3">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-medium text-violet-200">Lignes supplémentaires (manuelles)</h3>
                <Button data-testid="admin-tasks-gen-add-manual-line" size="sm" variant="outline" className="border-violet-500/40 bg-violet-500/10 text-violet-200 hover:bg-violet-500/20" onClick={addManualLine}>+ Ajouter une ligne</Button>
              </div>
              {manualLines.length > 0 && (
                <div className="overflow-x-auto rounded-lg border border-white/10">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="min-w-[160px]">Description</TableHead>
                        <TableHead className="min-w-[140px]">Commentaire</TableHead>
                        <TableHead className="w-[130px]">Date</TableHead>
                        <TableHead className="w-[80px]">Qté</TableHead>
                        <TableHead className="w-[110px]">PU TTC</TableHead>
                        <TableHead className="w-[50px]"></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {manualLines.map((ml, idx) => (
                        <TableRow key={ml._tmp_id || idx} data-testid={`admin-tasks-manual-line-${idx}`}>
                          <TableCell>
                            <Input data-testid={`admin-tasks-manual-desc-${idx}`} className="border-white/10 bg-white/[0.08]" value={ml.description} onChange={(e) => updateManualLine(idx, { description: e.target.value })} placeholder="Description" />
                          </TableCell>
                          <TableCell>
                            <Input data-testid={`admin-tasks-manual-comment-${idx}`} className="border-white/10 bg-white/[0.08]" value={ml.comment || ""} onChange={(e) => updateManualLine(idx, { comment: e.target.value })} placeholder="Commentaire" />
                          </TableCell>
                          <TableCell>
                            <Input data-testid={`admin-tasks-manual-date-${idx}`} type="date" className="border-white/10 bg-white/[0.08]" value={ml.date || ""} onChange={(e) => updateManualLine(idx, { date: e.target.value })} />
                          </TableCell>
                          <TableCell>
                            <Input data-testid={`admin-tasks-manual-qty-${idx}`} type="number" min="0" step="0.5" className="border-white/10 bg-white/[0.08]" value={ml.qty} onChange={(e) => updateManualLine(idx, { qty: e.target.value })} />
                          </TableCell>
                          <TableCell>
                            <Input data-testid={`admin-tasks-manual-pu-${idx}`} type="number" min="0" step="0.01" className="border-white/10 bg-white/[0.08]" value={ml.unit_price_ttc} onChange={(e) => updateManualLine(idx, { unit_price_ttc: e.target.value })} />
                          </TableCell>
                          <TableCell>
                            <Button data-testid={`admin-tasks-manual-remove-${idx}`} variant="ghost" size="sm" className="text-rose-300 hover:bg-rose-500/10 hover:text-rose-200" onClick={() => removeManualLine(idx)}>×</Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </div>

            <div>
              <h3 className="text-sm font-medium text-slate-200 mb-2">Lignes facturées ({genLines.length})</h3>
              <div className="rounded-xl border border-white/10 bg-white/[0.04] overflow-x-auto">
                <Table data-testid="admin-tasks-gen-lines-table">
                  <TableHeader>
                    <TableRow>
                      <TableHead>Description</TableHead>
                      <TableHead>Commentaire</TableHead>
                      <TableHead className="w-[70px]">Qté</TableHead>
                      <TableHead className="w-[120px]">PU TTC</TableHead>
                      <TableHead className="w-[120px]">Total TTC</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {genLines.map((l, idx) => (
                      <TableRow key={l.id || `gen-${idx}`} data-testid={`admin-tasks-gen-line-${idx}`}>
                        <TableCell>{l.description}{l.is_free && <span className="ml-2 text-xs text-emerald-300">(offert)</span>}</TableCell>
                        <TableCell className="text-sm text-slate-300">{l.comment || ""}</TableCell>
                        <TableCell>{l.qty}</TableCell>
                        <TableCell>{l.unit_price_ttc.toFixed(2)} €</TableCell>
                        <TableCell>{lineTtc(l).toFixed(2)} €</TableCell>
                      </TableRow>
                    ))}
                    {!genLines.length && (
                      <TableRow><TableCell colSpan={5} className="py-6 text-center text-slate-300">Aucune ligne.</TableCell></TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>
            </div>

            <div className="flex items-center justify-end gap-4 rounded-xl border border-white/10 bg-white/[0.04] px-4 py-3 text-sm">
              <div className="text-slate-300">HT : <strong className="text-slate-100">{totalsHT.toFixed(2)} €</strong></div>
              <div className="text-slate-300">TVA {vatPct}% : <strong className="text-slate-100">{totalsTVA.toFixed(2)} €</strong></div>
              <div className="text-violet-300">TTC : <strong className="text-violet-200" data-testid="admin-tasks-gen-total-ttc">{totalsTTC.toFixed(2)} €</strong></div>
            </div>
          </div>

          {/* Aperçu PDF + actions post-génération (étape 2 : envoi email) */}
          {generatedInvoice && (
            <div data-testid="admin-tasks-gen-preview" className="mt-4 space-y-3 rounded-xl border border-emerald-500/30 bg-emerald-500/5 p-4">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div>
                  <div className="text-sm font-medium text-emerald-200">✅ Facture générée — {generatedInvoice.amount_ttc.toFixed(2)} € TTC</div>
                  <div className="text-xs text-slate-300">Vérifiez l&apos;aperçu ci-dessous, puis envoyez par email au propriétaire (ou fermez pour envoyer plus tard depuis l&apos;onglet Factures).</div>
                </div>
                <div className="flex flex-wrap gap-2">
                  <Button data-testid="admin-tasks-gen-download" variant="outline" className="rounded-full border-white/10 bg-white/[0.08] text-slate-100 hover:bg-white/10" onClick={downloadGeneratedInvoice}>
                    ⬇️ Télécharger
                  </Button>
                  <Button data-testid="admin-tasks-gen-send-email" className="rounded-full bg-emerald-500 text-white hover:bg-emerald-400 disabled:opacity-50" onClick={sendInvoiceEmail} disabled={sendingEmail}>
                    {sendingEmail ? "Envoi..." : "📧 Envoyer par email au propriétaire"}
                  </Button>
                  <Button data-testid="admin-tasks-gen-cancel-invoice" variant="outline" className="rounded-full border-rose-500/40 bg-rose-500/10 text-rose-200 hover:bg-rose-500/20" onClick={cancelGeneratedInvoice}>
                    🗑️ Annuler cette facture
                  </Button>
                </div>
              </div>
              <div className="relative">
                <object
                  data-testid="admin-tasks-gen-pdf-object"
                  data={generatedInvoice.pdf_url}
                  type="application/pdf"
                  className="h-[60vh] w-full rounded-lg border border-white/10 bg-white"
                >
                  {/* Fallback natif : le navigateur affiche ce bloc si le PDF viewer ne peut pas rendre inline */}
                  <div
                    data-testid="admin-tasks-gen-pdf-fallback"
                    className="flex flex-col items-center justify-center gap-3 rounded-lg border border-white/10 bg-slate-900 p-8 text-center h-[60vh]"
                  >
                    <div className="text-4xl">📄</div>
                    <div className="text-sm text-slate-200">
                      L&apos;aperçu inline n&apos;est pas supporté par votre navigateur.
                    </div>
                    <Button
                      data-testid="admin-tasks-gen-pdf-fallback-download"
                      onClick={downloadGeneratedInvoice}
                      className="rounded-full bg-violet-500 text-white hover:bg-violet-400"
                    >
                      ⬇️ Télécharger le PDF pour vérification
                    </Button>
                  </div>
                </object>
              </div>
            </div>
          )}

          <DialogFooter className="gap-2 mt-2">
            <Button data-testid="admin-tasks-gen-cancel" variant="outline" className="rounded-full border-white/10 bg-white/[0.08] text-slate-50 hover:bg-white/10" onClick={closeGenerateDialog}>
              {generatedInvoice ? "Fermer" : "Annuler"}
            </Button>
            {!generatedInvoice && (
              <Button data-testid="admin-tasks-gen-submit" className="rounded-full bg-violet-500 text-white hover:bg-violet-400" onClick={submitGenerate} disabled={genSubmitting}>
                {genSubmitting ? "Génération..." : "📄 Générer la facture"}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* AlertDialog Radix — confirmation d'annulation d'une facture générée */}
      <AlertDialog open={confirmCancelOpen} onOpenChange={(o) => { if (!o && !cancelling) setConfirmCancelOpen(false); }}>
        <AlertDialogContent
          data-testid="admin-tasks-cancel-invoice-confirm"
          className="border-white/10 bg-[#0a1428] text-slate-50"
        >
          <AlertDialogHeader>
            <AlertDialogTitle data-testid="admin-tasks-cancel-invoice-confirm-title" className="text-slate-100">
              Annuler cette facture ?
            </AlertDialogTitle>
            <AlertDialogDescription data-testid="admin-tasks-cancel-invoice-confirm-desc" className="text-slate-300">
              {generatedInvoice?.email_sent
                ? "⚠️ Cette facture a déjà été envoyée au propriétaire. L'annulation est définitive et sera tracée dans l'audit."
                : "La facture sera définitivement supprimée. L'action est irréversible et sera tracée dans l'audit."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="gap-2">
            <AlertDialogCancel
              data-testid="admin-tasks-cancel-invoice-confirm-no"
              className="rounded-full border-white/10 bg-white/[0.08] text-slate-100 hover:bg-white/10"
              disabled={cancelling}
            >
              Non, garder
            </AlertDialogCancel>
            <AlertDialogAction
              data-testid="admin-tasks-cancel-invoice-confirm-yes"
              className="rounded-full bg-rose-500 text-white hover:bg-rose-400 disabled:opacity-50"
              disabled={cancelling}
              onClick={(e) => { e.preventDefault(); performCancelInvoice(); }}
            >
              {cancelling ? "Annulation..." : "Oui, annuler la facture"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Édition d'une ligne manuelle persistée (depuis la table d'extraction) */}
      <Dialog open={editMlOpen} onOpenChange={(open) => { if (!open) { setEditMlOpen(false); setEditMl(null); } else { setEditMlOpen(true); } }}>
        <DialogContent data-testid="admin-tasks-edit-manual-line-dialog" className="border-white/10 bg-[#0a1428] text-slate-50 sm:max-w-[520px]">
          <DialogHeader>
            <DialogTitle data-testid="admin-tasks-edit-manual-line-title">Modifier la ligne manuelle</DialogTitle>
          </DialogHeader>
          {editMl && (
            <div className="space-y-3">
              <div className="space-y-1">
                <Label className="text-xs text-slate-300">Description</Label>
                <Input
                  data-testid="admin-tasks-edit-manual-desc"
                  className="border-white/10 bg-white/[0.08]"
                  value={editMl.description}
                  onChange={(e) => setEditMl((s) => ({ ...s, description: e.target.value }))}
                />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div className="space-y-1">
                  <Label className="text-xs text-slate-300">Quantité</Label>
                  <Input
                    data-testid="admin-tasks-edit-manual-qty"
                    type="number" min="0" step="0.5"
                    className="border-white/10 bg-white/[0.08]"
                    value={editMl.qty}
                    onChange={(e) => setEditMl((s) => ({ ...s, qty: e.target.value }))}
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs text-slate-300">Prix unitaire TTC (€)</Label>
                  <Input
                    data-testid="admin-tasks-edit-manual-price"
                    type="number" min="0" step="0.01"
                    className="border-white/10 bg-white/[0.08]"
                    value={editMl.unit_price_ttc}
                    onChange={(e) => setEditMl((s) => ({ ...s, unit_price_ttc: e.target.value }))}
                  />
                </div>
              </div>
              <div className="space-y-1">
                <Label className="text-xs text-slate-300">Commentaire</Label>
                <Input
                  data-testid="admin-tasks-edit-manual-comment"
                  className="border-white/10 bg-white/[0.08]"
                  value={editMl.comment}
                  onChange={(e) => setEditMl((s) => ({ ...s, comment: e.target.value }))}
                />
              </div>
              <div className="rounded-md bg-white/[0.05] p-2 text-xs text-slate-300">
                Total TTC : <strong className="text-slate-100">{((Number(editMl.qty) || 0) * (Number(editMl.unit_price_ttc) || 0)).toFixed(2)} €</strong>
              </div>
            </div>
          )}
          <DialogFooter className="gap-2">
            <Button data-testid="admin-tasks-edit-manual-cancel" variant="outline" className="rounded-full border-white/10 bg-white/[0.08] text-slate-50 hover:bg-white/10" onClick={() => { setEditMlOpen(false); setEditMl(null); }}>Annuler</Button>
            <Button data-testid="admin-tasks-edit-manual-save" className="rounded-full bg-violet-500 text-white hover:bg-violet-400" onClick={saveEditedManualLine}>Enregistrer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
